import { useState, useEffect, useCallback } from 'react';
import { SalesOrder, SalesOrderInstallment } from '../../../../types/sales.types';
import {
  SalesOrderInstallment as APISalesOrderInstallment,
  GenerateInstallmentsDto,
} from '../../../../api';
import { salesService } from '../../../../services/sales.service';
import { INSTALLMENT_STATUS_CONFIG, INSTALLMENT_LIMITS } from '../../../../utils/constants';
import { formatCurrency as formatCurrencyValue } from '../../../../utils/formatters/money';

interface NewInstallmentData {
  numberOfInstallments: number;
  frequency: GenerateInstallmentsDto.frequency;
  firstDueDate: string;
  installmentAmount: number;
  notes: string;
}

interface UseInstallmentsModalProps {
  open: boolean;
  order: SalesOrder | null;
  onInstallmentsUpdated?: () => void;
}

export const useInstallmentsModal = ({
  open,
  order,
  onInstallmentsUpdated,
}: UseInstallmentsModalProps) => {
  const [installments, setInstallments] = useState<SalesOrderInstallment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pendingBalance, setPendingBalance] = useState(0);

  // New installment form state
  const [newInstallmentData, setNewInstallmentData] = useState<NewInstallmentData>({
    numberOfInstallments: INSTALLMENT_LIMITS.DEFAULT,
    frequency: GenerateInstallmentsDto.frequency.MONTHLY,
    firstDueDate: '',
    installmentAmount: 0,
    notes: '',
  });

  // Map API installment to local type
  const mapAPIInstallmentToLocal = useCallback(
    (apiInstallment: APISalesOrderInstallment): SalesOrderInstallment => ({
      id: apiInstallment.id,
      order: order!,
      payment: undefined,
      installmentNumber: apiInstallment.installmentNumber,
      installmentTotal: apiInstallment.installmentTotal,
      dueDate: new Date(apiInstallment.dueDate),
      amount: apiInstallment.amount,
      status: apiInstallment.status as any,
      paidDate: apiInstallment.paidDate ? new Date(apiInstallment.paidDate) : undefined,
      lateFee: apiInstallment.lateFee,
      notes: apiInstallment.notes,
      createdAt: new Date(apiInstallment.createdAt),
    }),
    [order],
  );

  // Load installments
  const loadInstallments = useCallback(async () => {
    if (!order) return;

    setIsLoading(true);
    setError(null);

    try {
      const orderInstallments = await salesService.getInstallmentsByOrder(order.id);
      const mappedInstallments = orderInstallments.map(mapAPIInstallmentToLocal);
      setInstallments(mappedInstallments);

      // Check if order already has installments to decide whether to show create form
      setShowCreateForm(orderInstallments.length === 0);
    } catch (err: any) {
      console.error('Error loading installments:', err);
      setError('Error al cargar las cuotas');
    } finally {
      setIsLoading(false);
    }
  }, [order, mapAPIInstallmentToLocal]);

  // Load installments when modal opens
  useEffect(() => {
    if (open && order) {
      loadInstallments();
    }
  }, [open, order, loadInstallments]);

  // Initialize form when order changes
  useEffect(() => {
    if (order) {
      const paidAmount = order.payments?.reduce((sum, p) => sum + p.paymentAmount, 0) || 0;
      const pending = order.totalAmount - paidAmount;
      setPendingBalance(pending);

      setNewInstallmentData((prev) => ({
        ...prev,
        installmentAmount: pending / prev.numberOfInstallments,
      }));

      // Set default first due date to next month
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setNewInstallmentData((prev) => ({
        ...prev,
        firstDueDate: nextMonth.toISOString().split('T')[0],
      }));
    }
  }, [order]);

  // Recalculate installment amount when number of installments changes
  useEffect(() => {
    if (newInstallmentData.numberOfInstallments > 0) {
      setNewInstallmentData((prev) => ({
        ...prev,
        installmentAmount: pendingBalance / prev.numberOfInstallments,
      }));
    }
  }, [newInstallmentData.numberOfInstallments, pendingBalance]);

  // Handle generate installments
  const handleGenerateInstallments = useCallback(async () => {
    if (!order) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate form
      if (newInstallmentData.numberOfInstallments <= 0) {
        throw new Error('El número de cuotas debe ser mayor a 0');
      }

      if (!newInstallmentData.firstDueDate) {
        throw new Error('Debe seleccionar la fecha de la primera cuota');
      }

      if (newInstallmentData.installmentAmount <= 0) {
        throw new Error('El monto de la cuota debe ser mayor a 0');
      }

      const request = {
        salesOrderId: order.id,
        totalInstallments: newInstallmentData.numberOfInstallments,
        frequency: newInstallmentData.frequency,
        firstInstallmentDate: newInstallmentData.firstDueDate,
        totalAmount: pendingBalance,
        lateFeePerInstallment: 0,
        notes: newInstallmentData.notes,
      };

      await salesService.generateInstallments(request);

      setSuccess('Plan de cuotas generado con éxito.');
      setShowCreateForm(false);

      // Reload installments
      await loadInstallments();
      onInstallmentsUpdated?.();
    } catch (err: any) {
      console.error('Error generating installments:', err);
      setError(err.message || 'Error al generar las cuotas');
    } finally {
      setIsLoading(false);
    }
  }, [order, newInstallmentData, pendingBalance, loadInstallments, onInstallmentsUpdated]);

  // Handle mark as paid
  const handleMarkAsPaid = useCallback(
    async (installmentId: number) => {
      setIsLoading(true);
      setError(null);

      try {
        await salesService.markInstallmentAsPaid(installmentId);
        setSuccess('Cuota marcada como pagada');

        // Reload installments
        await loadInstallments();
        onInstallmentsUpdated?.();
      } catch (err: any) {
        console.error('Error marking installment as paid:', err);
        setError('Error al marcar la cuota como pagada');
      } finally {
        setIsLoading(false);
      }
    },
    [loadInstallments, onInstallmentsUpdated],
  );

  // Handle cancel installment
  const handleCancelInstallment = useCallback(
    async (installmentId: number) => {
      setIsLoading(true);
      setError(null);

      try {
        await salesService.cancelInstallment(installmentId);
        setSuccess('Cuota cancelada');

        // Reload installments
        await loadInstallments();
        onInstallmentsUpdated?.();
      } catch (err: any) {
        console.error('Error cancelling installment:', err);
        setError('Error al cancelar la cuota');
      } finally {
        setIsLoading(false);
      }
    },
    [loadInstallments, onInstallmentsUpdated],
  );

  // Get status chip config
  const getStatusChip = useCallback((status: string, dueDate: Date) => {
    const isOverdue = new Date() > new Date(dueDate) && status === 'PENDING';

    if (isOverdue) {
      return {
        color: 'error' as const,
        label: 'Vencida',
        icon: INSTALLMENT_STATUS_CONFIG.OVERDUE.icon,
      };
    }

    return (
      INSTALLMENT_STATUS_CONFIG[status as keyof typeof INSTALLMENT_STATUS_CONFIG] ||
      INSTALLMENT_STATUS_CONFIG.PENDING
    );
  }, []);

  // Format currency (Bolivianos)
  const formatCurrency = useCallback((amount: number) => formatCurrencyValue(amount), []);

  // Format date
  const formatDate = useCallback((date: Date | string) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, []);

  // Get days until due
  const getDaysUntilDue = useCallback((dueDate: Date) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  // Get installments summary
  const getInstallmentsSummary = useCallback(() => {
    const total = installments.length;
    const paid = installments.filter((i) => i.status === 'PAID').length;
    const pending = installments.filter((i) => i.status === 'PENDING').length;
    const overdue = installments.filter((i) => {
      const isOverdue = new Date() > new Date(i.dueDate) && i.status === 'PENDING';
      return isOverdue || i.status === 'OVERDUE';
    }).length;

    return { total, paid, pending, overdue };
  }, [installments]);

  // Update form field
  const updateFormField = useCallback((field: keyof NewInstallmentData, value: any) => {
    setNewInstallmentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  return {
    // State
    installments,
    isLoading,
    error,
    success,
    showCreateForm,
    setShowCreateForm,
    newInstallmentData,
    pendingBalance,
    // Handlers
    handleGenerateInstallments,
    handleMarkAsPaid,
    handleCancelInstallment,
    updateFormField,
    // Helpers
    getStatusChip,
    formatCurrency,
    formatDate,
    getDaysUntilDue,
    getInstallmentsSummary,
  };
};
